import React, { useCallback, useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ConnectedProps } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faUserCircle } from "@fortawesome/pro-solid-svg-icons";
import { faPencil } from "@fortawesome/pro-light-svg-icons";
import {
  FootEnum,
  Image,
  PlayerPositionEnum,
  PlayerSearchElement,
  type PlayerSearchElementList,
  PlayerSquadElement,
  Squad
} from "../../../../client/api";
import { ClavaContext } from "../../../../config/contexts";
import AsideModal from "../../../components/AsideModal";
import { showTranslated, translate } from "../../../../config/translator";
import { errorLogging, nameRegexTest } from "../../../../config/utils";
import ClavaTextInput from "../../../components/ClavaTextInput";
import ClavaDropdown, { DropdownItem } from "../../../components/ClavaDropdown";
import { connector } from "./redux";
import ClavaImage from "../../../components/ClavaImage";
import useBoolState from "../../../../hooks/useBoolState";
import ClavaButton from "../../../components/ClavaButton";
import FileUploader from "../../../components/FileUploader/FileUploader";
import client, { callClientFunc } from "../../../../client";
import { trackAmplitude } from "../../../../config/amplitude";

const loadingGif = require("../../../../assets/images/loading.gif");

const CreateEditPlayer: React.FC<ConnectedProps<typeof connector>> = ({
                                                                        player, teamId,
                                                                        close, positions, getPositions
                                                                      }) => {
  const { l, user } = useContext(ClavaContext);
  const [loading, setLoading] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [foundPlayers, setFoundPlayers] = useState<PlayerSearchElementList>([]);
  const [selectedPlayer, setSelectedPlayer] = useState<PlayerSearchElement>();
  const [ignoreDuplicate, setIgnoreDuplicate] = useState(false);
  const [imagePicker, toggleImagePicker] = useBoolState(false);
  const [familyName, setFamilyName] = useState(player?.player?.familyName ?? "");
  const familyNameInvalid = useRef(false);
  const [givenName, setGivenName] = useState(player?.player?.givenName ?? "");
  const givenNameInvalid = useRef(false);
  const [birthday, setBirthday] = useState(player?.player?.birthdate ?? "");
  const birthdayInvalid = useRef(false);
  const [position, setPosition] = useState<PlayerPositionEnum | undefined>(player?.player?.playerPosition?.key ?? PlayerPositionEnum.MIDFIELD);
  const [foot, setFoot] = useState<string | undefined>(player?.player?.foot ?? undefined);
  const [height, setHeight] = useState((player?.player?.height ?? "").toString(10));
  const heightInvalid = useRef(false);
  const footItems = useMemo(() => [
    {
      key: FootEnum.BOTH,
      label: translate("BOTH", l)
    } as DropdownItem<string>,
    {
      key: FootEnum.LEFT,
      label: translate("LEFT", l)
    } as DropdownItem<string>,
    {
      key: FootEnum.RIGHT,
      label: translate("RIGHT", l)
    } as DropdownItem<string>
  ], [l]);
  const onClose = useCallback(() => {
    close();
  }, [close]);
  const [image, setImage] = useState(player?.player?.photo);
  const onSetImage = useCallback((img: Image) => {
    setImage(img);
    toggleImagePicker();
  }, [toggleImagePicker]);
  useEffect(() => {
    if (positions.length === 0) {
      getPositions();
    }
  }, [positions.length, getPositions]);
  const positionItems = useMemo(() => positions.map<DropdownItem<PlayerPositionEnum>>(p => ({
      key: p.key,
      label: showTranslated(p.translation, l)
    })
  ), [l, positions]);
  useEffect(() => {
    if (!player && givenName.length > 1 && familyName.length > 1) {
      callClientFunc(client().searchPlayer, false, `${givenName} ${familyName}`, user.areaOfInterest.id).then((res) => {
        setFoundPlayers(res);
        setIgnoreDuplicate(false);
        setSelectedPlayer(undefined);
      }, errorLogging);
    }
  }, [familyName, givenName, player, user.areaOfInterest.id]);
  const errorStateActive = useRef(false);
  const saveDisabled = useMemo(() => {
    if (birthday.length !== 0) {
      const d = new Date(birthday);
      if (Number.isNaN(d.getTime())) {
        birthdayInvalid.current = true;
        return true;
      }
    }
    const familyInvalid = familyName.length < 2 || !nameRegexTest(familyName);
    const givenInvalid = givenName.length < 2 || !nameRegexTest(givenName);
    const h = parseInt(height, 10);
    heightInvalid.current = height.length !== 0 && (Number.isNaN(h) || h < 0 || h > 300);
    familyNameInvalid.current = familyInvalid;
    givenNameInvalid.current = givenInvalid;
    const basic = familyInvalid || givenInvalid || !position;
    if (player) {
      const playerHeight = player.player.height ?? 0;
      const inputHeight = height.length !== 0 ? h : 0;
      const playerFoot = player.player.foot ?? undefined;
      return basic ||
        (image?.id === player.player.photo?.id
          && position === player.player.playerPosition.key
          && familyName === player.player.familyName
          && givenName === player.player.givenName
          && birthday === player.player.birthdate
          && playerFoot === foot
          && inputHeight === playerHeight
        );
    }
    return basic || foundPlayers.length !== 0 && !selectedPlayer && !ignoreDuplicate;

  }, [birthday, familyName, givenName, height, position, player, foundPlayers.length, selectedPlayer, ignoreDuplicate, image?.id, foot]);

  useLayoutEffect(() => {
    if (familyName || givenName) {
      familyNameInvalid.current = false;
      givenNameInvalid.current = false;
    }
  }, [familyName, givenName]);
  const onSave = useCallback(() => {
    errorStateActive.current = true;
    if (loading || saveDisabled)
      return;
    if (player) {
      setLoading(true);
      trackAmplitude("tm_patch_player_finish", { teamId });
      callClientFunc(client().patchPlayer, false, player.player.id, {
        familyName,
        birthdate: birthday.length === 0 ? player.player.birthdate : birthday,
        givenName,
        foot: player.player.foot !== foot ? foot as FootEnum : undefined,
        height: player.player.height !== parseInt(height, 10) ? parseInt(height, 10) : undefined,
        photoId: image?.id
      }, teamId, position !== player.player.playerPosition.key ? position : undefined).then((res) => {
        close({
          ...player,
          player: {
            ...(res as unknown as PlayerSquadElement),
            playerPosition: res.teamPositions[0].playerPosition
          }
        });
      }, errorLogging).finally(() => {
        setLoading(false);
      });

    } else if (position) {
      if (selectedPlayer) {
        setLoading(true);
        callClientFunc(client().addPlayerToTeam, false, selectedPlayer.id, teamId, position).then(() => {
          callClientFunc(client().getSingleSquad, false, teamId, selectedPlayer.id).then((sq) => {
            close(sq);
          }, errorLogging).finally(() => {
            setLoading(false);
          });
        }, () => {
          setLoading(false);
        });
      } else {
        setLoading(true);
        trackAmplitude("tm_create_player_finish", { teamId });
        callClientFunc(client().createPlayer, false, {
          givenName,
          familyName,
          birthdate: birthday.length === 0 ? undefined : birthday,
          mainTeamId: teamId,
          teamPositions: [{ position, teamId }],
          foot: foot as FootEnum | undefined,
          height: Number.isNaN(parseFloat(height)) ? undefined : parseFloat(height)
        }).then((res) => {
          if (image) {
            client().patchPlayer(res.id, {
              photoId: image.id
            }, teamId, undefined).then((res2) => {
              close({
                matches: 0,
                minutes: 0,
                goals: 0,
                id: Math.floor(Math.random() * 10000),
                player: {
                  ...(res2 as unknown as PlayerSquadElement),
                  playerPosition: res2.teamPositions[0].playerPosition
                }
              } as Squad);
            }, errorLogging).finally(() => {
              setLoading(false);
            });
          } else {
            close({
              matches: 0,
              minutes: 0,
              goals: 0,
              id: Math.floor(Math.random() * 10000),
              player: {
                ...(res as unknown as PlayerSquadElement),
                playerPosition: res.teamPositions[0].playerPosition
              }
            } as Squad);
          }

        }, errorLogging).finally(() => {
          if (!image)
            setLoading(false);
        });
      }
    }
  }, [foot, height, loading, saveDisabled, player, position, familyName, givenName, birthday, image, close, selectedPlayer, teamId]);
  const onDelete = useCallback(() => {
    if (player) {
      setDeleting(true);
      trackAmplitude("tm_delete_player", { player: player.player.id, team: teamId });
      callClientFunc(client().removePlayerFromTeam, false, player.player.id, teamId).then(() => {
        close(player.player.id);
      }, errorLogging).finally(() => {
        setDeleting(false);
      });
    }
  }, [close, player, teamId]);
  const onTransfer = useCallback(() => {
    if (!player && selectedPlayer && selectedPlayer.mainTeam && position) {
      setLoading(true);
      trackAmplitude("tm_transfer_player", { player: selectedPlayer.id, newTeam: teamId });
      callClientFunc(client().doTransfer, false, selectedPlayer.id, selectedPlayer.mainTeam.id, teamId, position).then((res) => {
        close({
          matches: 0,
          minutes: 0,
          goals: 0,
          id: Math.floor(Math.random() * 10000),
          player: {
            ...(res as unknown as PlayerSquadElement),
            playerPosition: res.teamPositions[0].playerPosition
          }
        } as Squad);
      }, errorLogging).finally(() => {
        setLoading(false);
      });
    }
  }, [close, player, position, selectedPlayer, teamId]);
  useEffect(() => {
    trackAmplitude(player ? "tm_edit_player" : "tm_create_player", player ? {
      player: player.player.id,
      teamId
    } : { teamId });

  }, [teamId, player]);
  return (
    <AsideModal close={onClose} title={translate("newPlayer", l)} saveClb={onSave}
                saveDisabled={saveDisabled || loading}
                saveText={translate(selectedPlayer ? "bothTeams" : "save", l)}
                loading={loading}>
      <div className="my-6 flex flex-col items-center justify-center">
        {image ? (
          <ClavaImage width={128} image={image}
                      className="rounded-full" />) : (selectedPlayer && selectedPlayer.photo) ? (
          <ClavaImage width={128} image={selectedPlayer.photo} className="rounded-full" />
        ) : (
          <FontAwesomeIcon icon={faUserCircle}
                           className="w-32 h-32 text-font-600 dark:text-font-600-dark" />
        )}
        <button onClick={toggleImagePicker} type="button"
                className="mt-2 text-font dark:text-font-dark py-0.5 px-3 rounded-full border border-light-gray dark:border-light-gray-dark hover:bg-primary-10 flex flex-row items-center justify-center">
          {translate("edit", l)}
          <FontAwesomeIcon icon={faPencil} className="w-4 h-4 ml-2" />
        </button>
      </div>
      <ClavaTextInput className="mt-4" onChange={setGivenName}
                      error={givenNameInvalid.current && errorStateActive.current} value={givenName}
                      label={`${translate("givenName", l)} *`} placeholder={translate("givenName", l)} />
      <ClavaTextInput className="mt-4" onChange={setFamilyName}
                      error={familyNameInvalid.current && errorStateActive.current}
                      value={familyName}
                      label={`${translate("familyName", l)} *`} placeholder={translate("familyName", l)} />

      {!ignoreDuplicate ? foundPlayers.map(pl => (
        <button type="button" key={`search-player-${pl.id}`} onClick={() => {
          if (pl.mainTeam?.id !== teamId)
            setSelectedPlayer(pl);
        }}
                className={`rounded-lg border px-2 my-2 border-light-gray dark:border-light-gray-dark h-10 flex flex-row items-center justify-start ${selectedPlayer?.id === pl.id ? "bg-primary-10" : "hover:bg-bg dark:hover:bg-bg-dark"} ${pl.mainTeam?.id === teamId && "bg-loss"}`}>
          {pl.photo ? (
            <ClavaImage width={32} image={pl.photo} className="mr-2 rounded-full" />
          ) : (
            <FontAwesomeIcon icon={faUserCircle}
                             className="w-8 h-8 mr-2 text-font-600 dark:text-font-600-dark" />
          )}
          <span>{`${pl.givenName} ${pl.familyName}${pl.mainTeam ? ` | ${showTranslated(pl.mainTeam.name, l)}` : ""}`}</span>
        </button>
      )) : null}

      <ClavaDropdown onChange={setPosition} withoutChoose items={positionItems} value={position}
                     label={`${translate("position", l)} *`}
                     containerClassName="mt-4"
                     className="h-10" />
      <ClavaTextInput className="mt-4" onChange={setBirthday}
                      error={birthdayInvalid.current && errorStateActive.current} type="date"
                      value={birthday}
                      label={translate("birthday", l)} />
      <div className="mt-4 flex flex-row items-center justify-start">
        <ClavaTextInput onChange={setHeight} error={heightInvalid.current} type="number" value={height}
                        label={translate("height", l)}
                        className="mr-2 flex-1 min-w-0" />
        <ClavaDropdown onChange={setFoot} value={foot} items={footItems} withoutChoose
                       label={translate("preferredFoot", l)}
                       containerClassName="flex-1 min-w-0"
                       className="h-10" />
      </div>
      <div className="flex-1" />
      {!!player && (
        <div className="self-center">
          <ClavaButton onClick={onDelete} disabled={deleting} size="fit" danger>
            {deleting ? (
              <img src={loadingGif} alt="Loading" className="w-4 h-4" />
            ) : (
              translate("deletePlayer", l)
            )}
          </ClavaButton>
        </div>
      )}
      {foundPlayers.length !== 0 && !selectedPlayer && !ignoreDuplicate ? (
        <div className="my-2 flex flex-col items-center justify-center">
          <span className="text-loss text-center block mb-2">{translate("hasDuplicate", l)}</span>
          <ClavaButton onClick={() => {
            setIgnoreDuplicate(true);
          }} size="fit">{translate("ignoreDuplicate", l)}</ClavaButton>
        </div>
      ) : foundPlayers.length !== 0 && selectedPlayer ? (
        <div className="my-2 flex flex-col items-center justify-center">
          <ClavaButton onClick={onTransfer} disabled={saveDisabled || loading || !selectedPlayer.mainTeam}
                       size="fit">{loading ? (
            <img src={loadingGif} alt="Loading"
                 className="w-4 h-4" />) : translate("transfer", l)}</ClavaButton>
        </div>
      ) : null}
      {imagePicker && (
        <FileUploader as={1} type="image" callback={onSetImage} close={toggleImagePicker} />
      )}
    </AsideModal>
  );
};

export default connector(CreateEditPlayer);
